import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "../../api";
import { Card, EmptyState } from "../../ui";
import { todayISO } from "../../DateInput";
import { EMPTY_MEETING, MeetingFields, meetingPayload } from "./MeetingForm";
import { MeetingCard } from "./MeetingCard";
import { MeetingEditModal } from "./MeetingEditModal";

const NewMeetingForm = ({ onCreated }) => {
  const [form, setForm] = useState(EMPTY_MEETING);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/meetings", meetingPayload(form));
      toast.success("Reunião agendada!");
      setForm(EMPTY_MEETING);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <Card className="p-5 mb-5">
      <form data-testid="new-meeting-form" onSubmit={submit} className="grid md:grid-cols-2 gap-3">
        <MeetingFields form={form} setForm={setForm} prefix="meeting" minDate={todayISO()} />
        <div className="md:col-span-2">
          <button data-testid="meeting-submit"
            className="px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]">
            Agendar reunião
          </button>
        </div>
      </form>
    </Card>
  );
};

export function MeetingsTab({ isAdmin }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(
    () =>
      api
        .get("/portal/meetings")
        .then((r) => setItems(r.data))
        .catch((err) => {
          setItems([]);
          toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (m) => {
    if (!window.confirm(`Excluir "${m.title}"? As pautas vinculadas voltarão para "pendente".`)) return;
    try {
      await api.delete(`/admin/meetings/${m.id}`);
      toast.success("Reunião excluída.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div>
      {isAdmin && (
        <div className="mb-4">
          <button data-testid="new-meeting-btn" onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]">
            <Plus size={14} /> Agendar reunião
          </button>
        </div>
      )}
      {open && isAdmin && (
        <NewMeetingForm onCreated={() => { setOpen(false); load(); }} />
      )}
      {items === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhuma reunião agendada"
          hint={isAdmin
            ? "Crie a primeira reunião acima — depois designe pautas a ela."
            : "Aguarde a administração agendar a próxima reunião."}
        />
      ) : (
        <ul className="space-y-3">
          {items.map((m) => (
            <MeetingCard key={m.id} m={m} isAdmin={isAdmin} onEdit={setEditing} onDelete={remove} />
          ))}
        </ul>
      )}
      {editing && (
        <MeetingEditModal meeting={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
